// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/JobOpeningForm.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';
import SelectDropdown from '../../customStyles/SelectDropdown';

const JobOpeningForm = ({ job, jobId, onClose, onSave }) => {
  const navigate = useNavigate();
  const { portalName } = useParams();
  const id = jobId;
  const isEditMode = !!id;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '', 
    responsibilities: '', 
    departmentId: '',
    divisionId: '',
    employmentType: 'full_time',
    workMode: 'onsite',
    location: '',
    numberOfOpenings: 1,
    experienceMin: 0,
    experienceMax: '',
    salaryRangeMin: '',
    salaryRangeMax: '',
    skills: '', 
    isPublic: true
  });

  // Reusable helper to prevent numeric scrolling on mousewheel and block minus/plus/scientific keys
  const preventNumberScrollAndNegative = {
    onWheel: (e) => e.target.blur(),
    onKeyDown: (e) => ["-", "+", "e"].includes(e.key) && e.preventDefault()
  };

  const fetchDropdowns = useCallback(async () => {
    try {
      const [deptRes, divRes] = await Promise.all([
        api.get(SummaryApi.getAllDepartments.url),
        api.get(SummaryApi.getAllDivisions.url)
      ]);
      if (deptRes.data.success) setDepartments(deptRes.data.data || []);
      if (divRes.data.success) setDivisions(divRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load form choices');
    }
  }, []);

  const fetchJobData = useCallback(async () => {
    if (!isEditMode) return;
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getRecruitmentJobById.url(id));
      if (response.data.success) {
        const data = response.data.data || response.data.job;
        setFormData({
          title: data.title || '',
          description: data.description || '',
          requirements: data.requirements?.join('\n') || '',
          responsibilities: data.responsibilities?.join('\n') || '',
          departmentId: data.departmentId?._id || data.departmentId || '',
          divisionId: data.divisionId?._id || data.divisionId || '',
          employmentType: data.employmentType || 'full_time',
          workMode: data.workMode || 'onsite',
          location: data.location || '',
          numberOfOpenings: data.numberOfOpenings || 1,
          experienceMin: data.experienceMin || 0,
          experienceMax: data.experienceMax || '',
          salaryRangeMin: data.salaryRangeMin || '',
          salaryRangeMax: data.salaryRangeMax || '',
          skills: data.skills?.join(', ') || '',
          isPublic: data.isPublic !== false
        });
      }
    } catch (err) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    fetchDropdowns();
    fetchJobData();
  }, [fetchDropdowns, fetchJobData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.departmentId) {
      toast.warning('Please select a department');
      return;
    }

    setSaving(true);
    const processedData = {
      ...formData,
      requirements: formData.requirements.split('\n').filter(line => line.trim() !== ''),
      responsibilities: formData.responsibilities.split('\n').filter(line => line.trim() !== ''),
      skills: formData.skills.split(',').map(s => s.trim()).filter(s => s !== ''),
      experienceMax: formData.experienceMax ? Number(formData.experienceMax) : undefined,
      salaryRangeMin: formData.salaryRangeMin ? Number(formData.salaryRangeMin) : undefined,
      salaryRangeMax: formData.salaryRangeMax ? Number(formData.salaryRangeMax) : undefined,
    };

    try {
      const apiCall = isEditMode 
        ? api.put(SummaryApi.updateRecruitmentJob.url(id), processedData)
        : api.post(SummaryApi.createRecruitmentJob.url, processedData);

      const response = await apiCall;
      if (response.data.success) {
        toast.success(isEditMode ? 'Job Opening updated successfully' : 'Job Opening created successfully');
        
        // Triggers the onSave callback to close the form and refresh parent table logs
        if (onSave) {
          onSave(); 
        } else {
          // Fallback navigation
          navigate(`/${portalName}/hr/recruitment-management?tab=jobs`);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save job opening');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500 font-semibold">Loading job configurations...</div>;
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6 flex items-center gap-2">
        <button 
          onClick={onClose} 
          type="button"
          className="rounded-lg bg-red-50 border border-red-100 p-2 text-textPrimary hover:text-primary transition-colors"
        >
          <FaArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Job Opening' : 'Create Job Opening'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        
        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-gray-700 mb-1">Job Title *</label>
            <input
              type="text"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="e.g. Senior NodeJS Developer"
            />
          </div>
          <div>
            <SelectDropdown label="Department *" options={departments} value={formData.departmentId} onChange={(val) => handleDropdownChange('departmentId', val)} valueKey="_id" labelKey="name" searchable />
          </div>
          <div>
            <SelectDropdown label="Division" options={divisions} value={formData.divisionId} onChange={(val) => handleDropdownChange('divisionId', val)} valueKey="_id" labelKey="name" searchable />
          </div>
        </div>

        {/* Configurations */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <SelectDropdown
              label="Employment Type"
              options={[
                { label: 'Full Time', val: 'full_time' },
                { label: 'Part Time', val: 'part_time' },
                { label: 'Contract', val: 'contract' },
                { label: 'Intern', val: 'intern' },
                { label: 'Consultant', val: 'consultant' }
              ]}
              value={formData.employmentType}
              onChange={(val) => handleDropdownChange('employmentType', val)}
              valueKey="val"
              labelKey="label"
            />
          </div>
          <div>
            <SelectDropdown
              label="Work Mode"
              options={[
                { label: 'Onsite', val: 'onsite' },
                { label: 'Remote', val: 'remote' },
                { label: 'Hybrid', val: 'hybrid' }
              ]}
              value={formData.workMode}
              onChange={(val) => handleDropdownChange('workMode', val)}
              valueKey="val"
              labelKey="label"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Work Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="e.g. Chennai, India"
            />
          </div>
        </div>

        {/* Quantities & Parameters (Numeric fields secured with event prevent helpers) */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">No. of Openings</label>
            <input
              type="number"
              name="numberOfOpenings"
              min="1"
              value={formData.numberOfOpenings}
              onChange={handleChange}
              {...preventNumberScrollAndNegative}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Min Exp (Yrs)</label>
            <input
              type="number"
              name="experienceMin"
              min="0"
              value={formData.experienceMin}
              onChange={handleChange}
              {...preventNumberScrollAndNegative}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Max Exp (Yrs)</label>
            <input
              type="number"
              name="experienceMax"
              min="0"
              value={formData.experienceMax}
              onChange={handleChange}
              {...preventNumberScrollAndNegative}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Min Salary (Annually)</label>
            <input
              type="number"
              name="salaryRangeMin"
              min="0"
              value={formData.salaryRangeMin}
              onChange={handleChange}
              {...preventNumberScrollAndNegative}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="e.g. 500000"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Max Salary (Annually)</label>
            <input
              type="number"
              name="salaryRangeMax"
              min="0"
              value={formData.salaryRangeMax}
              onChange={handleChange}
              {...preventNumberScrollAndNegative}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="e.g. 1200000"
            />
          </div>
        </div>

        {/* Text descriptions */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">Skills (Comma separated) *</label>
          <input
            type="text"
            name="skills"
            required
            value={formData.skills}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
            placeholder="React, NodeJS, Express, Mongo"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">Description</label>
          <textarea
            name="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Requirements (one per line) *</label>
            <textarea
              name="requirements"
              rows="4"
              value={formData.requirements}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="E.g. Solid understanding of ES6&#10;Experience in Git"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Responsibilities (one per line) *</label>
            <textarea
              name="responsibilities"
              rows="4"
              value={formData.responsibilities}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="E.g. Code feature enhancements&#10;Peer review"
            />
          </div>
        </div>

        {/* Visibility */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="isPublic"
            id="isPublic"
            checked={formData.isPublic}
            onChange={handleChange}
            className="h-4 w-4 accent-red-600 rounded cursor-pointer"
          />
          <label htmlFor="isPublic" className="text-xs font-bold text-gray-700 cursor-pointer">
            Make visible on the public careers page
          </label>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose} 
            className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-primary hover:bg-red-700 text-white font-semibold rounded-lg shadow-sm disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? 'Saving...' : 'Save Job Opening'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobOpeningForm;